import { Category, useAlert } from '@drawbotics/react-drylus';
import { RouterService } from '@tokamakjs/common';
import { Controller, onDidMount, state, useAppContext } from '@tokamakjs/react';

import { AuthGuard } from '~/guards/auth.guard';
import { AuthService } from '~/services';
import { AppContext, Invitation, InvitationStatus, UserFromToken } from '~/types';
import { createTranslate } from '~/utils';

import { CreateOrJoinCompanyAccountView } from './create-join-company.view';

const ttErrors = createTranslate('errors');
const tt = createTranslate('routes.create_join_company');

@Controller({ view: CreateOrJoinCompanyAccountView, guards: [AuthGuard] })
export class CreateOrJoinCompanyAccountController {
  @state userIsLoading = true;
  @state user?: UserFromToken;
  @state errorWhileLoadingUser = false;
  @state isSubmittingNewCompany = false;
  private _showAlert = useAlert().showAlert;
  private _appContext = useAppContext<AppContext>();

  invitationCode: string | null;
  @state processingInvitation?: boolean;
  @state invitation?: Invitation;

  constructor(router: RouterService, private _authService: AuthService) {
    this.invitationCode = router.query.get('code');
  }

  async processInvitation(invitationCode: string) {
    this.processingInvitation = true;

    const user = this.user;

    if (user == null) {
      throw new Error();
    }

    const invitationRes = await this._authService.fetchInvitation(invitationCode);

    if (invitationRes.error) {
      this._showAlert({
        text: tt('generic_invitation_error'),
        category: Category.DANGER,
      });

      this.processingInvitation = false;

      return;
    }

    const invitation = invitationRes.data;

    if (invitation.status === InvitationStatus.ACCEPTED) {
      this._appContext.onFinish();
      return;
    }

    if (user.organisation != null) {
      this._appContext.onFinish();
      return;
    }

    if (user.email !== invitation.email) {
      this._showAlert({
        text: tt('wrong_user'),
        category: Category.DANGER,
      });

      this.processingInvitation = false;

      return;
    }

    const joinCompanyRes = await this._authService.joinCompany(invitation.token);

    if (joinCompanyRes.error) {
      this._showAlert({
        text: tt('generic_join_company_error'),
        category: Category.DANGER,
      });

      this.processingInvitation = false;
    } else {
      this._appContext.onFinish();
    }
  }

  @onDidMount()
  async onDidMount() {
    const userFromToken = await this._authService.getUserFromMostRecentToken();
    this.userIsLoading = false;

    if (userFromToken.error) {
      this._showAlert({
        text: ttErrors('generic'),
        category: Category.DANGER,
      });

      this.errorWhileLoadingUser = true;

      return;
    } else {
      this.user = userFromToken.data;
      const organisation = this.user.organisation;

      // checking for object details due to backend issue
      if (
        organisation != null &&
        organisation.id != null &&
        organisation.name != null &&
        !this.invitationCode
      ) {
        this._appContext.onFinish();
        return;
      } else if (this.invitationCode) {
        await this.processInvitation(this.invitationCode);
      } else {
        return;
      }
    }
  }

  async createNewCompany(values: { name: string }) {
    this.isSubmittingNewCompany = true;

    const res = await this._authService.createCompany(values);

    if (res.error) {
      return this._showAlert({
        text: ttErrors('generic'),
        category: Category.DANGER,
      });
    } else {
      this._appContext.onFinish();
    }
  }
}
